import { X, Loader2, FileText, Mail, RefreshCw } from "lucide-react";

function ActionButtons({ emitting, onEmit, onRecalc, onResendPrEmail, canResendPrEmail, onRefreshCofidis, canRefreshCofidis }) {
  const btnPrimary = "px-3 py-1.5 text-xs rounded bg-[#0F1829] text-white hover:bg-[#1a2747] disabled:opacity-50 inline-flex items-center gap-1.5";
  const btnSecondary = "px-3 py-1.5 text-xs rounded border border-[#0F1829] text-[#0F1829] hover:bg-[#F5F2EB] disabled:opacity-50 inline-flex items-center gap-1.5";
  const btnDanger = "px-3 py-1.5 text-xs rounded border border-rose-700 text-rose-700 hover:bg-rose-50 disabled:opacity-50 inline-flex items-center gap-1.5";
  const btnSuccess = "px-3 py-1.5 text-xs rounded border border-emerald-700 text-emerald-700 hover:bg-emerald-50 disabled:opacity-50 inline-flex items-center gap-1.5";
  const btnInfo = "px-3 py-1.5 text-xs rounded border border-sky-700 text-sky-700 hover:bg-sky-50 disabled:opacity-50 inline-flex items-center gap-1.5";
  const btnAccent = "px-3 py-1.5 text-xs rounded border border-[#E60013] text-[#E60013] hover:bg-red-50 disabled:opacity-50 inline-flex items-center gap-1.5";
  return (
    <div className="mb-4 flex flex-wrap gap-2">
      <button onClick={() => onEmit('FT')} disabled={emitting} data-testid="emit-fatura-recibo" className={btnPrimary}>
        {emitting === 'FT' ? <Loader2 size={12} className="animate-spin" /> : <FileText size={12} />} Gerar Fatura/Recibo
      </button>
      <button onClick={() => onEmit('RB')} disabled={emitting} data-testid="emit-recibo" className={btnSecondary}>
        {emitting === 'RB' ? <Loader2 size={12} className="animate-spin" /> : <FileText size={12} />} Gerar Recibo
      </button>
      <button onClick={() => onEmit('NC')} disabled={emitting} data-testid="emit-nc" className={btnDanger}>
        {emitting === 'NC' ? <Loader2 size={12} className="animate-spin" /> : <FileText size={12} />} Gerar Nota Crédito
      </button>
      <button onClick={onRecalc} disabled={emitting} data-testid="recalc-commissions" className={btnSuccess}>
        {emitting === 'CALC' ? <Loader2 size={12} className="animate-spin" /> : <FileText size={12} />} Recalcular Comissões
      </button>
      {canResendPrEmail && (
        <button onClick={onResendPrEmail} disabled={emitting} data-testid="resend-pr-email" className={btnInfo}>
          {emitting === 'EMAIL_PR' ? <Loader2 size={12} className="animate-spin" /> : <Mail size={12} />} Reenviar email PR
        </button>
      )}
      {canRefreshCofidis && (
        <button onClick={onRefreshCofidis} disabled={emitting} data-testid="refresh-cofidis-link" className={btnAccent}>
          {emitting === 'COFIDIS' ? <Loader2 size={12} className="animate-spin" /> : <RefreshCw size={12} />} Regenerar link Cofidis
        </button>
      )}
    </div>
  );
}

function DocList({ title, items, type }) {
  const idKey = `ID_${type}`;
  const nrKey = `Nr${type}`;
  const dateKey = "Data";
  const moloniKey = `${idKey}_Moloni`;
  const urlKey = `Url${type === "Factura" ? "Fatura" : type}`;
  const empty = !items || items.length === 0;
  const isNC = type === "NotaCredito";
  return (
    <div className="mb-4">
      <p className="text-[10px] uppercase tracking-[0.18em] text-[#978D78] mb-1.5">{title}</p>
      {empty ? (
        <p className="text-xs text-[#6B7280] italic">Sem {title.toLowerCase()} emitid{title.endsWith("s") ? "as" : "o"}.</p>
      ) : (
        <ul className="space-y-1 text-sm">
          {items.map((it) => (
            <li key={it[idKey]} className={`flex items-center justify-between gap-2 p-2 rounded border border-[#E6E1D6] ${isNC ? "bg-rose-50" : "bg-[#FAF8F2]"}`}>
              <div>
                <p className="font-mono font-medium">{it[nrKey]}</p>
                <p className="text-[11px] text-[#978D78]">
                  {(it[dateKey] || '').slice(0, 16)} · Moloni #{it[moloniKey]}
                </p>
              </div>
              {it[urlKey] && (
                <a href={it[urlKey]} target="_blank" rel="noreferrer"
                  data-testid={type === "NotaCredito" ? undefined : `${type === "Factura" ? "fatura" : "recibo"}-link-${it[idKey]}`}
                  className={`px-2 py-1 text-xs rounded border transition ${isNC ? "border-rose-700 text-rose-700 hover:bg-rose-700 hover:text-white" : "border-[#0F1829] text-[#0F1829] hover:bg-[#0F1829] hover:text-white"}`}>
                  PDF
                </a>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export default function BillingModal({ order, data, loading, emitting, message, onClose, onEmit, onRecalc, onResendPrEmail, onRefreshCofidis }) {
  if (!order) return null;
  const paidStates = new Set(["PR", "PA", "FE", "EC", "EV", "DO"]);
  const state = (data?.current_state || "").toUpperCase();
  const canResendPrEmail = !!(data && paidStates.has(state));
  const canRefreshCofidis = !!(data && state === "AP" && (data.payment_method || "").toUpperCase() === "CP");
  return (
    <div className="fixed inset-0 bg-black/40 z-50 grid place-items-center p-4 overflow-y-auto" onClick={onClose} data-testid="billing-modal">
      <div onClick={(e) => e.stopPropagation()} className="bg-white rounded-lg shadow-2xl w-full max-w-2xl max-h-[calc(100dvh-2rem)] overflow-y-auto overscroll-contain">
        <div className="flex items-center justify-between px-5 py-3 border-b border-[#E6E1D6] bg-[#F5F2EB] sticky top-0 z-10">
          <div>
            <p className="text-[10px] uppercase tracking-[0.18em] text-[#978D78]">Faturação da encomenda</p>
            <p className="text-base font-semibold">#{order.ID_Encomenda} · {order.NomeCompleto || '—'}</p>
          </div>
          <button onClick={onClose} className="text-[#6B7280] hover:text-[#0F1829]"><X size={18} /></button>
        </div>
        <div className="p-5">
          {loading && <p className="text-sm text-[#6B7280]"><Loader2 size={14} className="inline animate-spin mr-2" /> A carregar...</p>}
          {!loading && data && (
            <>
              {data.pending_moloni && (
                <div className="mb-3 p-3 rounded border border-amber-200 bg-amber-50 text-xs text-amber-800">
                  A encomenda está em <strong>PR (Pagamento Recebido)</strong> mas ainda não há fatura emitida.
                  Use o botão "Gerar Fatura/Recibo" abaixo.
                </div>
              )}
              <ActionButtons emitting={emitting} onEmit={onEmit} onRecalc={onRecalc} onResendPrEmail={onResendPrEmail} canResendPrEmail={canResendPrEmail} onRefreshCofidis={onRefreshCofidis} canRefreshCofidis={canRefreshCofidis} />
              {message && (
                <div className={`mb-3 p-2 rounded text-xs ${message.success ? 'bg-emerald-50 text-emerald-800 border border-emerald-200' : 'bg-rose-50 text-rose-800 border border-rose-200'}`}
                  data-testid="emit-message">
                  {message.message || (message.success ? 'Documento gerado com sucesso.' : 'Erro ao gerar documento.')}
                </div>
              )}
              <DocList title="Faturas" items={data.facturas} type="Factura" />
              <DocList title="Recibos" items={data.recibos} type="Recibo" />
              {(data.notas_credito || []).length > 0 && (
                <DocList title="Notas de Crédito" items={data.notas_credito} type="NotaCredito" />
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
